import {
  chatLanguageDirection,
  groupRefusalEntries,
  type QuestionnaireChatMessage,
} from "@digital-twins/shared-types";

import { chatT } from "@/i18n";
import { builderCardText } from "../utils/builderCardCopy";
import {
  notDoneLines,
  removedObjectiveQuestionLines,
} from "../utils/notDoneCopy";
import { ruleLine } from "../utils/questionnaireIssueCopy";
import { languageName } from "../utils/turnCommandCopy";
import { userMessageReceiptLines } from "../utils/userMessageReceiptLines";

/** What a reply's lines are worded from. */
export type AssistantReplyMessage = Pick<
  QuestionnaireChatMessage,
  | "id"
  | "content"
  | "notDone"
  | "removedObjectiveQuestions"
  | "translatedInto"
  | "refusals"
  | "notInStudyLanguage"
  | "collect"
  | "language"
  | "receipts"
  | "writtenAnswer"
  | "answerOnly"
> &
  // Only a stopped turn's status line (`system_status`) leads with "Nothing was changed."
  Partial<Pick<QuestionnaireChatMessage, "kind">>;

/**
 * A stopped turn's refusals as the builder words them: each rule's line
 * (`ruleLine`, which throws for a code with none, never drops it), with
 * the questions it affects when it names some, in the message's language. A
 * status line (a turn that changed nothing) leads with "Nothing was changed.";
 * a cap's question (a clarifying question) is the lines alone.
 */
function refusalLine(
  message: Pick<AssistantReplyMessage, "refusals" | "kind">,
  lng: string | undefined,
): string {
  const t = chatT(lng);
  const lines = groupRefusalEntries(
    (message.refusals ?? []).map(({ code, params, questions }) => ({
      line: ruleLine(code, params, lng),
      questions: questions ?? [],
    })),
  ).map(({ line, questions }) =>
    questions.length > 0
      ? `${line} ${t("questionnaire.refusalAffects", { questions: questions.join(", ") })}`
      : line,
  );
  return [
    ...(message.kind === "system_status"
      ? [t("questionnaire.notDone.nothingChanged")]
      : []),
    ...lines,
  ].join(" ");
}

/**
 * A reply's lines, the one composition every assistant message reads: its
 * bubble (`ChatBubble`) and the card a reply with an upload carries
 * (`CollectCard`, gate 2026-10-15 PR-09). A reply's content is the writer's
 * own text; under it, the code lines: what the turn did not do, and the
 * questions an objective edit kept for a removed objective, typed on the
 * message and worded here (F16, F24). What the commit changed (an objective,
 * a reworded title, the answers removed, what a rule wrote beside it) is its
 * receipts, worded once by Nest and shown outside and under the bubble
 * (`ReplyReceipts`, D2, wave I C-15, C-16). A message a limit stopped is the
 * writer's answer, if any, then the limit's own lines.
 */
export function assistantReplyLines(
  message: AssistantReplyMessage,
  chainCode: string | null | undefined,
  assetPanelHeading: string,
): Array<string> {
  // The language the message was written in: every code line of it is worded
  // in it (wave G, D). A message stored before it was kept reads in the
  // interface language.
  const lng = message.language;
  const t = chatT(lng);
  const lines = [
    // A translation's reply is fixed: the builder's words, in the
    // interface language; its `content` is the commit's fixed line.
    message.translatedInto
      ? t("questionnaire.translatedReply", {
          language: languageName(message.translatedInto, lng),
        })
      : // A question Nest asks by a catalog rule is the rule's line, in the
        // interface language; `content` is its English copy.
        message.refusals
        ? [message.writtenAnswer, refusalLine(message, lng)]
            .filter(Boolean)
            .join("\n\n")
        : builderCardText(message, chainCode, t),
    ...(message.notDone
      ? notDoneLines(
          message.notDone,
          assetPanelHeading,
          lng,
          Boolean(message.collect),
        )
      : []),
    ...removedObjectiveQuestionLines(message.removedObjectiveQuestions ?? [], t),
    // Material a chat revision or re-plan added in another language is
    // kept as written (C12-1): the reply's typed receipt, as a paste's.
    ...userMessageReceiptLines(
      { notInStudyLanguage: message.notInStudyLanguage },
      t,
    ),
  ];
  // A writer that failed leaves no text: the code lines stand alone.
  return lines.filter((line, index) => index > 0 || line !== "");
}

/**
 * What a commit changed (`receipts`, worded by Nest from its record in the
 * chat's language), under the reply's bubble and never inside it (D2): the
 * same assistant bubble, a second one in the same message. None when the
 * message has no receipt.
 */
export function ReplyReceipts(props: {
  message: Pick<AssistantReplyMessage, "id" | "receipts" | "language">;
}) {
  const { message } = props;
  const receipts = message.receipts ?? [];
  if (receipts.length === 0) return null;
  return (
    <div
      data-testid="chat-message-receipts"
      dir={chatLanguageDirection(message.language)}
      className="mt-1 block w-fit max-w-full rounded-2xl rounded-tl-sm bg-study-chat-ai-bubble px-4 py-2.5 text-base leading-[1.6] text-start text-foreground [overflow-wrap:anywhere]"
    >
      <ReplyLines id={`${message.id}-receipts`} text={receipts.join("\n")} />
    </div>
  );
}

/** A message's lines as a bubble shows them: a `- ` line is a list item. */
export function ReplyLines(props: { id: string; text: string }) {
  const { id, text } = props;
  return (
    <>
      {text.split("\n").map((line, index) => {
        const bulletMatch = /^[-*•]\s+(.*)$/.exec(line);
        if (bulletMatch) {
          return (
            <span key={`${id}-${index}`} className="flex gap-2">
              <span aria-hidden="true" className="shrink-0">
                •
              </span>
              <span className="min-w-0">{bulletMatch[1]}</span>
            </span>
          );
        }
        if (line.length === 0) {
          return (
            <span
              key={`${id}-${index}`}
              className="block h-2"
              aria-hidden="true"
            />
          );
        }
        return (
          <span key={`${id}-${index}`} className="block">
            {line}
          </span>
        );
      })}
    </>
  );
}
